import {
    RiAiGenerate,
    RiAlarmLine,
    RiBarChart2Line,
    RiCalendarTodoLine,
    RiDashboardLine,
    RiGroupLine,
    RiHashtag,
    RiLineChartLine, RiPieChartLine,
    RiPriceTag3Line,
    RiSparklingLine,
    RiTableLine,
    RiThumbUpLine,
} from "@remixicon/react";
import React, { ReactElement, useMemo, useState } from "react";
import { MetricsDashboardGraph, MetricsDashboardGraphType, useGetReportGraphTemplatesQuery } from "../../../__generated__/api";
import { ReactComponent as AveragesIcon } from "../../../assets/icons/averages.svg";
import { ReactComponent as NumbersIcon } from "../../../assets/icons/numbers.svg";
import { ARTICLES } from "../../../components/help/Help";
import Template, { TemplateItem } from "../../../components/template/Template";
import Styles from "./AddWidgetTemplate.module.scss";

export const GRAPH_TYPES = {
    [MetricsDashboardGraphType.PieChart]: {
        icon: <RiPieChartLine />,
    },
    [MetricsDashboardGraphType.TimeSeries]: {
        icon: <RiCalendarTodoLine />,
    },
    [MetricsDashboardGraphType.Table]: {
        icon: <RiTableLine />,
    },
    [MetricsDashboardGraphType.Count]: {
        icon: <NumbersIcon />,
    },
    [MetricsDashboardGraphType.BarGraph]: {
        icon: <RiBarChart2Line />,
    },
    [MetricsDashboardGraphType.LineGraph]: {
        icon: <RiLineChartLine />,
    },
};

export const CATEGORY_ICONS: Record<string, ReactElement> = {
    Tickets: <RiPriceTag3Line className={Styles.icon} />,
    "Time Series": <RiCalendarTodoLine className={Styles.icon} />,
    "Pie Chart": <RiPieChartLine className={Styles.icon} />,
    Users: <RiGroupLine className={Styles.icon} />,
    Numbers: <NumbersIcon className={Styles.icon} />,
    Averages: <AveragesIcon className={Styles.icon} />,
    SLAs: <RiAlarmLine className={Styles.icon} />,
    Table: <RiTableLine className={Styles.icon} />,
    Tags: <RiHashtag className={Styles.icon} />,
    Feedback: <RiThumbUpLine className={Styles.icon} />,
    Deflection: <RiAiGenerate className={Styles.icon} />,
    AI: <RiSparklingLine className={Styles.icon} />,
};

export default function useAddWidgetTemplate({
    onSelected,
    onCreate,
}: {
    onSelected: (graph: MetricsDashboardGraph) => void,
    onCreate: () => void,
}) {
    const [shouldShow, setShouldShow] = useState(false);

    const templateQuery = useGetReportGraphTemplatesQuery();
    const templates = useMemo(() => templateQuery.data?.metricsDashboardGraphTemplates?.map(row => ({
        id: row.id,
        title: row.title,
        description: row.description,
        categories: row.categories,
        icon: GRAPH_TYPES[row.type]?.icon,
    }) as TemplateItem) ?? [], [templateQuery.data?.metricsDashboardGraphTemplates]);

    return {
        setShouldShow,
        element: (
            <Template
                helpText="How to create a Report Widget"
                helpArticleId={ARTICLES.REPORT_ADD_WIDGET}
                templates={templates}
                onUseTemplate={selected => {
                    const graph = templateQuery.data?.metricsDashboardGraphTemplates?.find(row => row.id === selected.id);
                    if (graph) {
                        onSelected({
                            ...graph,
                            description: undefined,
                            categories: undefined,
                        } as unknown as MetricsDashboardGraph);
                    }
                    setShouldShow(false);
                }}
                onCreate={() => {
                    setShouldShow(false);
                    onCreate();
                }}
                categoryIcons={CATEGORY_ICONS}
                categoryIcon={<RiDashboardLine />}
                title="Add Widget"
                shouldShow={shouldShow}
                onHide={() => setShouldShow(false)}
            />
        ),
    };
}
